import React, { useRef, useState, useEffect } from "react";

import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import ShareIcon from "@mui/icons-material/Share";

import SubtitleEditor from "./parts/SubtitleEditor";
import { Episode } from "../../services/episode.service";
import { VideoPlayerState } from "./types/VideoPlayerState";
import VideoPreviewPlayer, {
  VideoPreviewPlayerHandle,
} from "./parts/VideoPreviewPlayer";
import AudioPreviewPlayer, {
  AudioPreviewPlayerHandle,
} from "./parts/AudioPreviewPlayer";
import RoleBasedAccess from "../../../common/RoleBasedAccess";
import { RoleName } from "../../../common/enums/RoleName";

interface Props {
  episode?: Episode;
  episodeId?: string;
  isAudioFile?: boolean;
  onShareClick?: () => void;
  isExpandedView?: boolean;
}

export default function Preview({
  episode,
  episodeId,
  isAudioFile,
  onShareClick,
  isExpandedView = false,
}: Props) {
  const playerRef = useRef<VideoPreviewPlayerHandle | AudioPreviewPlayerHandle>(
    null,
  );
  const [videoPlayerState, setVideoPlayerState] = useState<VideoPlayerState>({
    isPlaying: false,
    currentTime: 0,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Video control methods
  const handleVideoPlay = () => {
    playerRef.current?.play();
  };

  const handleVideoPause = () => {
    playerRef.current?.pause();
  };

  // Monitor video player state changes
  useEffect(() => {}, [videoPlayerState]);

  if (isMobile) {
    // Mobile: Video first, then subtitles
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: 2,
        }}
      >
        <Box sx={{ width: "100%" }}>
          {episode?.upload?.url &&
            (isAudioFile ? (
              <AudioPreviewPlayer
                ref={playerRef}
                src={episode.upload.url}
                videoPlayerState={videoPlayerState}
                onUpdatePlayerState={setVideoPlayerState}
              />
            ) : (
              <VideoPreviewPlayer
                ref={playerRef}
                src={episode.upload.url}
                videoPlayerState={videoPlayerState}
                onUpdatePlayerState={setVideoPlayerState}
              />
            ))}
        </Box>
        <Box sx={{ width: "100%", mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Transcript
          </Typography>
          {episode && (
            <SubtitleEditor
              episode={episode}
              currentTime={videoPlayerState?.currentTime}
              onParagraphClick={(paragraph) => {
                playerRef.current?.seekTo(paragraph.start);
                setVideoPlayerState({
                  ...videoPlayerState,
                  currentTime: paragraph.start,
                });
              }}
              onVideoPlay={handleVideoPlay}
              onVideoPause={handleVideoPause}
            />
          )}
        </Box>
      </Box>
    );
  }

  const quickActionBtnSx = {
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    flex: 1,
    py: 1,
    textTransform: "none",
    "&:hover": {
      borderColor: "#2563eb",
      color: "#2563eb",
      backgroundColor: "rgba(37,99,235,0.04)",
    },
  };

  const mediaPlayerNode = (
    <Box
      sx={{
        width: isExpandedView ? "100%" : "100%",
        maxWidth: isExpandedView ? 680 : "none",
        mx: isExpandedView ? "auto" : 0,
        aspectRatio: "16 / 9",
        flexShrink: 0,
        overflow: "hidden",
        borderRadius: "12px",
        transition: "width 0.25s ease",
      }}
    >
      {episode?.upload?.url &&
        (isAudioFile ? (
          <AudioPreviewPlayer
            ref={playerRef}
            src={episode.upload.url}
            videoPlayerState={videoPlayerState}
            onUpdatePlayerState={setVideoPlayerState}
          />
        ) : (
          <VideoPreviewPlayer
            ref={playerRef}
            src={episode.upload.url}
            videoPlayerState={videoPlayerState}
            onUpdatePlayerState={setVideoPlayerState}
          />
        ))}
    </Box>
  );

  const transcriptNode = (
    <Box
      sx={{
        width: "100%",
        minHeight: isExpandedView ? 360 : 320,
        maxHeight: isExpandedView ? "62vh" : "48vh",
        overflow: "hidden",
        backgroundColor: "#fff",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        p: 2,
        boxSizing: "border-box",
      }}
    >
      {episode && (
        <SubtitleEditor
          episode={episode}
          currentTime={videoPlayerState?.currentTime}
          onParagraphClick={(paragraph) => {
            playerRef.current?.seekTo(paragraph.start);
            setVideoPlayerState({
              ...videoPlayerState,
              currentTime: paragraph.start,
            });
          }}
          onVideoPlay={handleVideoPlay}
          onVideoPause={handleVideoPause}
        />
      )}
    </Box>
  );

  const quickActionsNode = (
    <Box
      sx={{
        flexShrink: 0,
        backgroundColor: "#fff",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "12px",
        px: 2.5,
        pt: 1.5,
        pb: 2,
        width: isExpandedView ? "100%" : "100%",
        maxWidth: isExpandedView ? 680 : "none",
        ml: isExpandedView ? "auto" : 0,
        transition: "width 0.25s ease",
      }}
    >
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          color: "text.secondary",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          mb: 1.25,
        }}
      >
        Quick Actions
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        {!isAudioFile && (
          <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OndemandVideoIcon sx={{ fontSize: 16 }} />}
              href={`/episodes/${episodeId}/videos?openGenerate=true`}
              sx={quickActionBtnSx}
            >
              Generate Shorts
            </Button>
          </RoleBasedAccess>
        )}
        {isAudioFile && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<GraphicEqIcon sx={{ fontSize: 16 }} />}
            href={`/episodes/${episodeId}/audiograms?openGenerate=true`}
            sx={quickActionBtnSx}
          >
            Generate Audiograms
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
          onClick={onShareClick}
          disabled
          sx={quickActionBtnSx}
        >
          Share Episode
        </Button>
      </Box>
    </Box>
  );

  // Desktop: Video first, transcript below, then quick actions
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        overflowY: "auto",
        gap: 1.5,
      }}
    >
      {!isExpandedView && quickActionsNode}

      {isExpandedView ? (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "stretch",
            minHeight: 0,
          }}
        >
          <Box sx={{ flex: 1.35, minWidth: 0 }}>{transcriptNode}</Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 1.5,
            }}
          >
            {quickActionsNode}
            {mediaPlayerNode}
          </Box>
        </Box>
      ) : (
        <>
          {/* Media player */}
          {mediaPlayerNode}
          {/* Transcript below video */}
          {transcriptNode}
        </>
      )}
    </Box>
  );
}
